import template from "./entity-customer.html";
import "./entity-customer.less";

import {
	BUSINESS_CUSTOMERS, 
	FORM_ENTITIES,
	BUSINESS_CUSTOMER_TRANSACTIONS,
	FORM_BUSINESS_CHARGES,
	FORM_BUSINESS_PRODUCTS
} from "routes";

import {
	ENTITY_TRANSLATION_KEYS, 
	I18NextContext 
} from "../../../../../../services/i18nService";
import { globs } from "../../../../../../services/globs";
import {
	All, 
	Pattern 
} from "../../../../../../services/security/actionScopes";

export function controller($logger, api, appState, loadingService, securityService, uiHelpers, toastr, entityBusinessService) {
	const ctrl = this;

	ctrl.i18n = I18NextContext.Instance;
	ctrl.ENTITY_TRANSLATION_KEYS = ENTITY_TRANSLATION_KEYS;
	ctrl.currentCustomer = undefined;
	ctrl.runOnReady = [];

	ctrl.$onInit = () => {
		loadCustomers();

		setupSecurity();
	};

	function runWhenValidBox(func) {
		if(ctrl.businessEntityBox) {
			func();
		}
		else {
			ctrl.runOnReady.push(func);
		}

	}

	function setupSecurity() {
		ctrl.canCreateCustomerGlob = globs.customers.byType("*").create;
		ctrl.canCreateEntityCustomerGlob = globs.entityTypes.byType(ctrl.typeConfig.Code).customers.byType("*").create;
		ctrl.canCreateActionScope = new All([
			new Pattern(ctrl.canCreateCustomerGlob),
			new Pattern(ctrl.canCreateEntityCustomerGlob)
		]);
	}

	async function loadCustomers() {
		const canReadCustomer = securityService.isPatternAllowed(globs.customers.byType("*").read);
		if(!canReadCustomer) {
			ctrl.currentCustomer = undefined;
			return;
		}

		const businessSupportingEntityTypes = ctrl.allTypeConfigs
			.filter(t => t.Businesses.IsSupported === true)
			.map(t => t.Code);

		ctrl.allBusinessEntities = [];
		const getEntityPromises = [];
		for(const type of businessSupportingEntityTypes) {
			const ge = getEntities(type);
			getEntityPromises.push(ge);
		}

		const entityResults = await loadingService.watch(Promise.all(getEntityPromises));
		ctrl.allBusinessEntities = entityResults
			.flatMap(r => r[0].Entities)
			.map(r => { 
				const thisEntityType = ctrl.allTypeConfigs.find(c => c.Code === r.Type.Code);

				return {
					...r, 
					Type: {
						...r.Type,
						Code: thisEntityType.Code
					}
				};
			})
			.sort((a, b) => a.Name.localeCompare(b.Name));

		runWhenValidBox(() => {
			const selectionChoices = uiHelpers.setDisplay(
				ctrl.allBusinessEntities, 
				{ 
					recentlyUsedEnabled: false, 
					hideCode: true 
				}
			);

			ctrl.businessEntityBox.setChoices(selectionChoices, "value", "label", true);
		});

		await ctrl.setCurrentBusinessEntity(ctrl.allBusinessEntities[0].Id);
	}

	ctrl.setCurrentBusinessEntity = async(entityId) => {
		try {
			const item = ctrl.allBusinessEntities.find(i => (i.Id === parseInt(entityId)) || (i.Id === entityId));

			ctrl.currentEntity = item;
		
			if(ctrl.currentEntity === undefined) {
				ctrl.currentBusiness = undefined;
				ctrl.currentCustomer = undefined;
				ctrl.allTransactions = [];
				ctrl.invoiced = [];
				ctrl.nonInvoiced = [];

				return;
			}
			runWhenValidBox(() => ctrl.businessEntityBox.setValueByChoice(ctrl.currentEntity.Id.toString()));

			const getBusinesses = entityBusinessService.getBusinessesForEntity(appState.forms.selected.Id, ctrl.currentEntity.Id);
			const businesses = await loadingService.watch(getBusinesses);
			ctrl.currentBusiness = businesses.filter(b => b.IsActive === true)[0];

			if(ctrl.currentBusiness === undefined) {
				ctrl.currentCustomer = undefined;
				ctrl.allTransactions = undefined;
				ctrl.invoiced = undefined;
				ctrl.nonInvoiced = undefined;

				return;
			}

			const loadingProducts = loadProducts();
			const loadingCharges = loadCharges();

			const customers = await loadingService.watch(api.get(BUSINESS_CUSTOMERS(appState.forms.selected.Id, ctrl.currentBusiness.Business.Id, ctrl.entity.Id)));

			ctrl.currentCustomer = customers[0];
			if(ctrl.currentCustomer === undefined) {
				const newCustomer = await loadingService.watch(
					ctrl.post(
						BUSINESS_CUSTOMERS(appState.forms.selected.Id, ctrl.currentBusiness.Business.Id), 
						{
							Name: ctrl.entity.Name,
							Entities: [{ Id: ctrl.entity.Id }]
						}
					)
				);

				ctrl.currentCustomer = newCustomer;
			}

			const loadingPendingTransactions = api.get(BUSINESS_CUSTOMER_TRANSACTIONS(appState.forms.selected.Id, ctrl.currentBusiness.Business.Id, ctrl.currentCustomer.Id, { status: "Pending" }));
			const loadingInvoicedTransactions = api.get(BUSINESS_CUSTOMER_TRANSACTIONS(appState.forms.selected.Id, ctrl.currentBusiness.Business.Id, ctrl.currentCustomer.Id, { status: "Invoiced" }));
			const [pendingTransactions, invoicedTransactions, products, charges] = await loadingService.watch(
				Promise.all(
					[
						loadingPendingTransactions, 
						loadingInvoicedTransactions, 
						loadingProducts, 
						loadingCharges
					]
				)
			);
			ctrl.allProducts = products;

			const enrichTransaction = (t) => ({
				...t,
				Transaction: {
					...t,
					Product: t.Product !== undefined 
						? {
							...t.Product, 
							...products.find(pr => pr.Code === t.Product.Code)
						} : undefined,
					Fees: t.Fees !== undefined 
						? t.Fees.map(f => ({
							...f, 
							...charges.find(ch => ch.Code === f.Code)
						})) : undefined
				}
			});

			const isLegacyApi = pendingTransactions.length > 0 
				&& invoicedTransactions.length > 0 
				&& pendingTransactions.length === invoicedTransactions.length
				&& pendingTransactions.every(p => invoicedTransactions.some(i => i.Id === p.Id));

			if(isLegacyApi) {
				ctrl.invoiced = invoicedTransactions.map(enrichTransaction)
					.filter(t => t.Invoices?.length && t.Invoices.some(i => !i.State?.IsVoid))
					.sort((a, b) => new Date(a.created) < new Date(b.created));

				ctrl.nonInvoiced = pendingTransactions.map(enrichTransaction)
					.filter(t => !t.Invoices?.length || !t.Invoices.some(i => !i.State?.IsVoid))
					.sort((a, b) => new Date(a.Created) < new Date(b.Created));
			}
			else {
				ctrl.invoiced = invoicedTransactions.map(enrichTransaction)
					.sort((a, b) => new Date(a.created) < new Date(b.created));

				ctrl.nonInvoiced = pendingTransactions.map(enrichTransaction)
					.sort((a, b) => new Date(a.Created) < new Date(b.Created));
			}

			return;
		}
		catch(err) {
			$logger.exception(err, "There was an error attempting to load businesses on customer screen.");
			toastr.error("There was an error loading businesses");

			ctrl.currentBusiness = undefined;
			ctrl.currentCustomer = undefined;
			ctrl.allTransactions = [];
			ctrl.invoiced = [];
			ctrl.nonInvoiced = [];

			return;
		}
	};
	
	async function getEntities(entityType) {
		const query = [{
			Type: { Code: entityType }
		}];
	
		const additionalHeaders = { "X-Vesl-Features-Advanced-Entity-Query": true };
		try {
			return ctrl.post(FORM_ENTITIES(appState.forms.selected.Id), query, { headers: additionalHeaders });
		}
		catch(err) {
			$logger.exception(err, "There was an error attempting to load entities on customer screen.");
			toastr.error("There was an error loading entities");
		}
	}

	async function loadProducts() {
		ctrl.allBusinessProducts = await loadingService.watch(api.get(FORM_BUSINESS_PRODUCTS(appState.forms.selected.Id, ctrl.currentBusiness.Business.Id)));

		return ctrl.allBusinessProducts;
	}

	async function loadCharges() {
		ctrl.allBusinessCharges = await loadingService.watch(api.get(FORM_BUSINESS_CHARGES(appState.forms.selected.Id, ctrl.currentBusiness.Business.Id)));

		return ctrl.allBusinessCharges;
	}

	ctrl.setBusinessEntityBox = (box) => {
		ctrl.businessEntityBox = box;

		if(ctrl.businessEntityBox) {
			ctrl.runOnReady.forEach(func => func());
			ctrl.runOnReady = [];
		}
	};

	ctrl.setDisplay = (id) => {
		const item = ctrl.allBusinessEntities.find(i => (i.Id === parseInt(id)) || (i.Id === id));

		if(!item) {
			$logger.warn("Selected item not found as an option.");
		}

		return item.Name || item.Code;
	};
}

export default angular.module("vesl.components.entity-customer", [])
	.component("entityCustomer", {
		controller,
		template,
		bindings: {
			entity: "<",
			allTypeConfigs: "<",
			typeConfig: "<",
			put: "=",
			post: "="
		}
	})
	.name;
